import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Header } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  Loader2,
  CheckCircle,
  XCircle,
  DollarSign,
  Calendar,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminSSBPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    effective_date: '',
    brackets: [],
    is_active: false,
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/ssb-rules`);
      setRules(response.data);
    } catch (error) {
      toast.error('Failed to fetch SSB rules');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setSelectedRule(rule);
      setFormData({
        name: rule.name,
        effective_date: rule.effective_date,
        brackets: rule.brackets,
        is_active: rule.is_active,
      });
    } else {
      setSelectedRule(null);
      // Default Belize SSB brackets with FIXED AMOUNTS (not percentages)
      setFormData({
        name: '',
        effective_date: new Date().toISOString().split('T')[0],
        brackets: [
          { min_income: 0, max_income: 69.99, employee_ssb: 0, employer_ssb: 0 },
          { min_income: 70, max_income: 109.99, employee_ssb: 2.45, employer_ssb: 4.55 },
          { min_income: 110, max_income: 139.99, employee_ssb: 3.85, employer_ssb: 7.15 },
          { min_income: 140, max_income: 179.99, employee_ssb: 4.90, employer_ssb: 9.10 },
          { min_income: 180, max_income: 219.99, employee_ssb: 6.30, employer_ssb: 11.70 },
          { min_income: 220, max_income: 259.99, employee_ssb: 7.70, employer_ssb: 14.30 },
          { min_income: 260, max_income: 299.99, employee_ssb: 9.10, employer_ssb: 16.90 },
          { min_income: 300, max_income: 339.99, employee_ssb: 10.50, employer_ssb: 19.50 },
          { min_income: 340, max_income: 379.99, employee_ssb: 11.90, employer_ssb: 22.10 },
          { min_income: 380, max_income: 419.99, employee_ssb: 13.30, employer_ssb: 24.70 },
          { min_income: 420, max_income: 459.99, employee_ssb: 14.70, employer_ssb: 27.30 },
          { min_income: 460, max_income: 499.99, employee_ssb: 16.10, employer_ssb: 29.90 },
          { min_income: 500, max_income: 99999, employee_ssb: 17.50, employer_ssb: 32.50 },
        ],
        is_active: false,
      });
    }
    setDialogOpen(true);
  };

  const handleBracketChange = (index, field, value) => {
    const newBrackets = [...formData.brackets];
    newBrackets[index] = {
      ...newBrackets[index],
      [field]: parseFloat(value) || 0,
    };
    setFormData({ ...formData, brackets: newBrackets });
  };

  const handleAddBracket = () => {
    const lastBracket = formData.brackets[formData.brackets.length - 1];
    setFormData({
      ...formData,
      brackets: [
        ...formData.brackets,
        {
          min_income: lastBracket ? lastBracket.max_income + 0.01 : 0,
          max_income: lastBracket ? lastBracket.max_income + 100 : 99.99,
          employee_ssb: 0,
          employer_ssb: 0,
        },
      ],
    });
  };

  const handleRemoveBracket = (index) => {
    const newBrackets = formData.brackets.filter((_, i) => i !== index);
    setFormData({ ...formData, brackets: newBrackets });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedRule) {
        await axios.put(`${API_URL}/admin/ssb-rules/${selectedRule.id}`, formData);
        toast.success('SSB rule updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/ssb-rules`, formData);
        toast.success('SSB rule created successfully');
      }
      setDialogOpen(false);
      fetchRules();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SSB rule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="SSB Configuration"
        subtitle="Manage Social Security Board deduction rules"
        actions={
          <Button onClick={() => handleOpenDialog()} className="gap-2" data-testid="add-ssb-rule-btn">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Rule Set</span>
          </Button>
        }
      />

      <div className="flex-1 p-4 lg:p-8 space-y-6">
        {/* Info Card */}
        <Card className="card-base bg-muted/50 border-dashed">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium">About SSB Configuration</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure Social Security Board deduction brackets for Belize using <strong>fixed BZD amounts</strong> (not percentages).
                  Only one rule set can be active at a time. Historical payrolls retain the SSB rule that was active when they were processed.
                  SSB is calculated once per worker per week based on their total gross pay.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rules List */}
        <div className="space-y-4">
          {rules.map((rule, index) => (
            <Card 
              key={rule.id} 
              className={`card-base animate-slide-up ${rule.is_active ? 'ring-2 ring-primary' : ''}`}
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`ssb-rule-${rule.id}`}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    rule.is_active ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Shield className={`w-5 h-5 ${rule.is_active ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      {rule.name}
                      {rule.is_active && (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Effective: {formatDate(rule.effective_date)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenDialog(rule)}
                  data-testid={`edit-ssb-rule-${rule.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Income Range (BZD)</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Employee SSB</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Employer SSB</th>
                        <th className="text-right py-2 px-3 font-medium text-muted-foreground">Total SSB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rule.brackets.map((bracket, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0">
                          <td className="py-2 px-3">
                            {formatCurrency(bracket.min_income)} - {formatCurrency(bracket.max_income)}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-destructive">
                            {formatCurrency(bracket.employee_ssb)}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-muted-foreground">
                            {formatCurrency(bracket.employer_ssb)}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold">
                            {formatCurrency(bracket.employee_ssb + bracket.employer_ssb)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {rules.length === 0 && (
          <Card className="card-base">
            <CardContent className="p-8 text-center">
              <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                No SSB rules configured. Create your first rule set to get started.
              </p>
              <Button onClick={() => handleOpenDialog()} className="mt-4" data-testid="create-first-ssb-rule">
                <Plus className="w-4 h-4 mr-2" />
                Create SSB Rule
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedRule ? 'Edit SSB Rule Set' : 'Create SSB Rule Set'}
            </DialogTitle>
            <DialogDescription>
              Configure income brackets with fixed BZD deduction amounts. Enter the exact dollar amount for each bracket, not percentages.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Rule Set Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Belize SSB 2024"
                    required
                    data-testid="ssb-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effective_date">Effective Date *</Label>
                  <Input
                    id="effective_date"
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    required
                    data-testid="ssb-date-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-input"
                  data-testid="ssb-active-checkbox"
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  Set as active rule (deactivates other rules)
                </Label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Income Brackets</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddBracket}
                    data-testid="add-bracket-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Bracket
                  </Button>
                </div>

                <div className="space-y-3">
                  {formData.brackets.map((bracket, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                      <div className="grid grid-cols-4 gap-2 flex-1">
                        <div>
                          <Label className="text-xs text-muted-foreground">Min (BZD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={bracket.min_income}
                            onChange={(e) => handleBracketChange(index, 'min_income', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Max (BZD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={bracket.max_income}
                            onChange={(e) => handleBracketChange(index, 'max_income', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Employee SSB (BZD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={bracket.employee_ssb}
                            onChange={(e) => handleBracketChange(index, 'employee_ssb', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Employer SSB (BZD)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={bracket.employer_ssb}
                            onChange={(e) => handleBracketChange(index, 'employer_ssb', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      {formData.brackets.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveBracket(index)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-ssb-rule-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Rule Set'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
